import { CURRENCY_SYMBOLS } from '@wagenow-ewa/common/dist/src/constants/currencies'
import { notification } from 'antd'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { chain, forEach, includes, isEmpty, lowerCase } from 'lodash'
import { ReactComponent as ErrorIcon } from '../assets/icons/error.svg'
import FlagCzechia from '../assets/icons/flag-czechia-rectangle.svg'
import FlagSlovakia from '../assets/icons/flag-slovakia-rectangle.svg'
import FlagUK from '../assets/icons/flag-united-kingdom-rectangle.svg'
import { ReactComponent as InfoIcon } from '../assets/icons/info.svg'
import { ReactComponent as SuccessIcon } from '../assets/icons/success.svg'
import { ReactComponent as WarningIcon } from '../assets/icons/warning.svg'
import { IErrorMessage } from '../types/interfaces'
import { ACCOUNT_STATE, FORM, NOTIFICATION_TYPE, USER_ROLE, USERS_CONTRACT_STATUS, WITHDRAWAL_STATE } from './enums'

export const showNotifications = (messages: IErrorMessage[], typeNotification: NOTIFICATION_TYPE) => {
	if (!isEmpty(messages) && typeNotification === NOTIFICATION_TYPE.NOTIFICATION) {
		forEach(messages, ({ type, message }) => {
			switch (lowerCase(type)) {
				case 'error':
					return notification.error({
						icon: <ErrorIcon />,
						message: <span className={'text-error-medium'}>{i18next.t('loc:general|Error') as string}</span>,
						description: message
					})
				case 'warning':
					return notification.warning({
						icon: <WarningIcon />,
						message: <span className={'text-warning-medium'}>{i18next.t('loc:general|Warning') as string}</span>,
						description: message
					})
				case 'success':
					return notification.success({
						icon: <SuccessIcon />,
						message: <span className={'text-success-medium'}>{i18next.t('loc:general|Success') as string}</span>,
						description: message
					})
				case 'info':
				default:
					return notification.info({
						icon: <InfoIcon />,
						message: <span className={'text-purple-base'}>{i18next.t('loc:general|Info') as string}</span>,
						description: message
					})
			}
		})
	}
}

export const formFieldID = (form?: FORM | string, name?: string) => {
	// NOTE: element can't be queried if id contains dots
	if (form && name) {
		const fieldSelector = chain(name)
			.filter((char) => char !== ']')
			.map((char) => (char === '[' || char === '.' ? '-' : char))
			.value()
			.join('')
		return `${form}-${fieldSelector}`
	}
	return undefined
}

export const mapUserRoleIDToEnum = (value: unknown) => {
	switch (value) {
		case 1:
		default:
			return USER_ROLE.EMPLOYEE
	}
}

export const mapUserRoleEnumToID = (value: unknown) => {
	switch (value) {
		case USER_ROLE.EMPLOYEE:
		default:
			return 1
	}
}

export const setAccountState = (value: unknown) => {
	return value && Object.values(ACCOUNT_STATE).includes(value as ACCOUNT_STATE) ? ACCOUNT_STATE[value as ACCOUNT_STATE] : ACCOUNT_STATE.INACTIVE
}

export const setWithdrawalState = (value: unknown) => {
	return value && Object.values(WITHDRAWAL_STATE).includes(value as WITHDRAWAL_STATE) ? WITHDRAWAL_STATE[value as WITHDRAWAL_STATE] : null
}

export const setDate = (value: unknown) => {
	return value && dayjs(String(value)).isValid() ? dayjs(String(value)) : undefined
}

export const formatAsCurrency = (value?: number, countryCode?: string) => {
	const currency = countryCode && countryCode.toLowerCase() === 'gb' ? 'GBP' : 'EUR'
	return `${String((value || 0).toFixed(2)).replace('.', ',')} ${CURRENCY_SYMBOLS[currency]}`
}

export const TELEPHONE_PREFIXES = {
	SK: {
		name: 'SK',
		flag: FlagSlovakia,
		prefix: '+421'
	},
	CZ: {
		name: 'CZ',
		flag: FlagCzechia,
		prefix: '+420'
	},
	UK: {
		name: 'UK',
		flag: FlagUK,
		prefix: '+44'
	}
}

/**
 * Get phone prefix based on country code
 * @param countryCode - ISO country code (e.g., 'sk', 'cz', 'gb')
 * @returns Phone prefix (e.g., '+421') or default '+421' if country not found
 */
export const getPhonePrefixByCountryCode = (countryCode?: string): string => {
	if (!countryCode) return '+421' // Default to Slovakia

	const normalizedCode = countryCode.toUpperCase()

	// Map country codes to TELEPHONE_PREFIXES keys
	// Handle both 'GB' and 'UK' for United Kingdom
	const countryMap: Record<string, keyof typeof TELEPHONE_PREFIXES> = {
		SK: 'SK',
		CZ: 'CZ',
		UK: 'UK',
		GB: 'UK' // Great Britain uses same prefix as UK
	}

	const prefixKey = countryMap[normalizedCode]
	return prefixKey ? TELEPHONE_PREFIXES[prefixKey].prefix : '+421' // Default to Slovakia
}

export const hiddenRequestError = (responseURL: string) => {
	// Return false to hide errors for these URLs
	if (includes(responseURL, 'onboarding-form')) return false
	if (includes(responseURL, 'contracts/') && includes(responseURL, 'share-link')) return false

	// Return true to show errors for all other URLs
	return true
}

export const isIOS = () => {
	return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

export const isMacOS = () => {
	return /Macintosh/.test(navigator.userAgent)
}

export const isWindows = () => {
	return /Windows/.test(navigator.userAgent)
}

export const isAndroid = () => {
	return /Android/.test(navigator.userAgent)
}

export const isInStandaloneMode = () => {
	return 'standalone' in (window as any).navigator && (window as any).navigator.standalone
}

// Browser detection functions
export const isChrome = () => {
	const { userAgent } = navigator
	return userAgent.includes('Chrome') && !userAgent.includes('Edg') && !userAgent.includes('OPR')
}

export const isEdge = () => {
	return navigator.userAgent.includes('Edg')
}

export const isFirefox = () => {
	return navigator.userAgent.includes('Firefox')
}

export const isOpera = () => {
	return navigator.userAgent.includes('OPR') || navigator.userAgent.includes('Opera')
}

export const isSafari = () => {
	const { userAgent } = navigator
	return userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Edg')
}

// Check if browser supports automatic PWA installation via beforeinstallprompt
export const supportsPWAInstallPrompt = () => {
	// Only Chrome and Edge actually support the beforeinstallprompt event
	// Firefox and Opera do NOT support this event as of 2024
	return isChrome() || isEdge()
}

// Check if browser supports reliable manual PWA installation
export const supportsPWAManualInstall = () => {
	// Only Safari has reliable manual PWA installation
	return isSafari()
}

// Check if browser is unsupported for PWA installation
export const isUnsupportedPWABrowser = () => {
	// Firefox, Opera and other browsers don't have reliable PWA installation
	return isFirefox() || isOpera() || (!isChrome() && !isEdge() && !isSafari())
}

// Check if manual installation instructions should be shown
export const shouldShowManualInstructions = () => {
	// Show manual instructions only for Safari
	return isSafari()
}

// Get the appropriate installation method for current browser
export const getInstallationMethod = () => {
	if (supportsPWAInstallPrompt()) {
		return 'automatic' // Chrome, Edge
	}
	if (supportsPWAManualInstall()) {
		return 'manual' // Safari
	}
	return 'unsupported' // Firefox, Opera, others
}

// Get browser category for PWA installation
export const getPWABrowserCategory = () => {
	if (isChrome() || isEdge()) {
		return 'supported-automatic'
	}
	if (isSafari()) {
		return 'supported-manual'
	}
	return 'unsupported'
}

export const getContractToSign = (contracts: { id: string; status: USERS_CONTRACT_STATUS; employeeContractId: string }[]) => {
	const completedContract = contracts.find((contract) => contract.status === USERS_CONTRACT_STATUS.COMPLETED)
	if (completedContract) return null

	const viewedContract = contracts.find((contract) => contract.status === USERS_CONTRACT_STATUS.VIEWED)
	if (viewedContract) return viewedContract

	const sentContract = contracts.find((contract) => contract.status === USERS_CONTRACT_STATUS.SENT)
	if (sentContract) return sentContract

	return null
}
